"use client";

import { useState, useEffect, useCallback } from "react";

// Global error handler for unhandled promise rejections
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    // Prevent default browser error logging
    event.preventDefault();
  });
}
import { fetchAdminDashboard } from "@/services/api/services/admin-dashboard";
import type { AdminDashboardData } from "@/services/api/services/admin-dashboard";

export function useDashboardData(refreshInterval = 30000) {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const dashboardData = await fetchAdminDashboard();
      setData(dashboardData);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch dashboard data");
      // Don't set data to undefined - let component use fallback data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    if (refreshInterval > 0) {
      const interval = setInterval(async () => {
        try {
          await fetchData();
        } catch (err) {
          // Prevent unhandled promise rejections
        }
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshInterval]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refresh: fetchData
  };
}